import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { getClientId } from '../../utils';
import { SESSION_KEY_LOCATION_ID } from '../../constants';

type Location = {
  uuid: string;
  region?: string;
  city?: string;
  name: string;
  latitude?: string;
  longitude?: string;
};

interface GetLocationsResponse {
  data: Location[];
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const eventsApiUrl = import.meta.env.VITE_EVENTS_API_URL;

function getDistanceFromLatLng(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function findClosestLocation(userLat: number, userLng: number, locations: Location[]): Location | null {
  let closestLocation: Location | null = null;
  let minDistance = Number.POSITIVE_INFINITY;

  for (const location of locations) {
    const lat = Number.parseFloat(location.latitude ?? '');
    const lng = Number.parseFloat(location.longitude ?? '');
    if (Number.isNaN(lat) || Number.isNaN(lng)) continue;

    const distance = getDistanceFromLatLng(userLat, userLng, lat, lng);
    if (distance < minDistance) {
      minDistance = distance;
      closestLocation = location;
    }
  }

  return closestLocation;
}

function sortByName(a: { name: string }, b: { name: string }): number {
  return a.name.localeCompare(b.name);
}

type GroupedRegion = { name: string; cities: { name: string; locations: Location[] }[] };

/** Same grouping as the legacy full-page picker: region → city → locations. */
function getLocationsGrouped(locations: Location[]): GroupedRegion[] {
  const groupedLocations: Record<string, Record<string, Location[]>> = {};

  locations.forEach((loc) => {
    const region = loc.region?.trim() || 'Other';
    const city = loc.city?.trim() || 'Other';
    if (!groupedLocations[region]) groupedLocations[region] = {};
    if (!groupedLocations[region][city]) groupedLocations[region][city] = [];
    groupedLocations[region][city].push(loc);
  });

  const finalGroup: GroupedRegion[] = [];
  Object.entries(groupedLocations).forEach(([regionName, cityData]) => {
    const citiesForThisRegion: GroupedRegion['cities'] = [];
    Object.entries(cityData).forEach(([cityName, cityLocations]) => {
      citiesForThisRegion.push({ name: cityName, locations: [...cityLocations].sort(sortByName) });
    });
    finalGroup.push({ name: regionName, cities: citiesForThisRegion.sort(sortByName) });
  });

  return finalGroup.sort(sortByName);
}

export default function LocationPickerModal({ open, onClose }: Props) {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationUUID, setSelectedLocationUUID] = useState<string>('default');
  const [isLocating, setIsLocating] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string>('');

  const groupedLocations = useMemo(() => getLocationsGrouped(locations), [locations]);
  const hasValidLocation = selectedLocationUUID !== 'default' && locations.some((location) => location.uuid === selectedLocationUUID);
  const selectedLocationName = useMemo(() => {
    if (selectedLocationUUID === 'default') return '';
    return locations.find((location) => location.uuid === selectedLocationUUID)?.name ?? '';
  }, [locations, selectedLocationUUID]);

  const selectItems = useMemo(() => {
    const items: React.ReactNode[] = [];
    groupedLocations.forEach((region) => {
      items.push(
        <ListSubheader key={`region-${region.name}`} disableSticky>
          {region.name}
        </ListSubheader>,
      );
      region.cities.forEach((city) => {
        items.push(
          <ListSubheader key={`city-${region.name}-${city.name}`} sx={{ pl: 2 }} disableSticky>
            {city.name}
          </ListSubheader>,
        );
        city.locations.forEach((location) => {
          items.push(
            <MenuItem key={location.uuid} value={location.uuid}>
              {location.name}
            </MenuItem>,
          );
        });
      });
    });
    return items;
  }, [groupedLocations]);

  useEffect(() => {
    if (!open) return;

    const initialLocation = localStorage.getItem(SESSION_KEY_LOCATION_ID) || 'default';
    setSelectedLocationUUID(initialLocation);
    setFeedbackMessage('');

    const fetchLocations = async () => {
      setIsLoadingLocations(true);
      try {
        const clientId = getClientId();
        if (!clientId) {
          setFeedbackMessage('Unable to load locations right now. Please choose again in a moment.');
          return;
        }

        const response = await axios.request<GetLocationsResponse>({
          url: '/locations/list/details',
          method: 'get',
          baseURL: eventsApiUrl,
          headers: { client_id: clientId },
        });

        const locationData = response.data?.data ?? [];
        setLocations(locationData);
      } catch (error) {
        setFeedbackMessage('Unable to load locations right now. Please try again shortly.');
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [open]);

  const handleFindMyLocation = () => {
    setFeedbackMessage('');
    if (!navigator.geolocation) {
      setFeedbackMessage('Geolocation is not supported by your browser. Please select a location manually.');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const closestLocation = findClosestLocation(position.coords.latitude, position.coords.longitude, locations);
        if (!closestLocation) {
          setFeedbackMessage('We could not match your location to an enabled campus. Please select one below.');
          setIsLocating(false);
          return;
        }

        setSelectedLocationUUID(closestLocation.uuid);
        setIsLocating(false);
      },
      () => {
        setFeedbackMessage('We could not access your location. Please allow location access or choose a location manually.');
        setIsLocating(false);
      },
    );
  };

  const handleTakeMeThere = () => {
    if (!hasValidLocation) {
      setFeedbackMessage('Please select a valid location before continuing.');
      return;
    }

    const clientId = getClientId();
    if (!clientId) {
      setFeedbackMessage('Unable to continue right now. Please refresh and try again.');
      return;
    }

    localStorage.setItem(SESSION_KEY_LOCATION_ID, selectedLocationUUID);
    window.location.href = `/?client_id=${clientId}&location_id=${selectedLocationUUID}`;
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setSelectedLocationUUID(event.target.value);
    setFeedbackMessage('');
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Choose Location</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Please select your location below:
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Button variant="outlined" onClick={handleFindMyLocation} disabled={isLocating || isLoadingLocations || locations.length === 0}>
            Find my location
          </Button>
          {isLocating && <CircularProgress size={18} />}
        </Box>

        {feedbackMessage && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {feedbackMessage}
          </Alert>
        )}

        <FormControl fullWidth>
          <InputLabel id="events-location-picker-label">Location</InputLabel>
          <Select
            labelId="events-location-picker-label"
            label="Location"
            value={selectedLocationUUID}
            onChange={handleSelectChange}
            disabled={isLoadingLocations}
            renderValue={(selected) => {
              if (selected === 'default') return 'Select your location';
              return selectedLocationName || 'Selected location';
            }}
          >
            <MenuItem value="default">Select your location</MenuItem>
            {selectItems}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleTakeMeThere} disabled={!hasValidLocation}>
          Take me there
        </Button>
      </DialogActions>
    </Dialog>
  );
}
